import { supabase } from '../lib/supabase';

const SETTINGS_TABLE = 'site_settings';

/**
 * Money-ish column -> number, or null when the column holds nothing usable.
 *
 * Deliberately does NOT fall back to a default. Substituting a default on read
 * and then writing the whole object back on save is how a stored rate silently
 * becomes a hardcoded one: the form shows a number the operator never chose,
 * and the next Save persists it as if it were real. A null here renders as an
 * empty field, which is honest — "not set" reads as not set.
 */
const numOrNull = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const n = typeof value === 'number' ? value : parseFloat(value);
  return Number.isFinite(n) ? n : null;
};

export const settingsService = {
  // Map DB row (snake_case) to app format (camelCase)
  mapDbToApp(row) {
    if (!row) return null;
    return {
      businessName: row.business_name ?? '',
      email: row.email ?? '',
      phone: row.phone ?? '',
      address: row.address ?? '',
      // Structural scaffold rather than a value: without it the hours editor
      // renders zero rows and there is no way to add them back.
      businessHours: row.business_hours ?? this.getDefaultSettings().businessHours,
      salesTaxRate: numOrNull(row.sales_tax_rate),
      docFee: numOrNull(row.doc_fee),
      defaultFinanceRate: numOrNull(row.default_finance_rate),
      emailNotifications: row.email_notifications ?? true,
      smsNotifications: row.sms_notifications ?? false,
      appointmentReminders: row.appointment_reminders ?? true,
      facebook: row.facebook ?? '',
      instagram: row.instagram ?? '',
      twitter: row.twitter ?? '',
      youtube: row.youtube ?? '',
      metaTitle: row.meta_title ?? this.getDefaultSettings().metaTitle,
      metaDescription: row.meta_description ?? this.getDefaultSettings().metaDescription,
      keywords: row.keywords ?? this.getDefaultSettings().keywords,
      id: row.id,
      created_at: row.created_at,
      updated_at: row.updated_at,
    };
  },

  // Map app format (camelCase) to DB row (snake_case).
  // Any field that is undefined or NaN is dropped from the payload, so a
  // partial or failed read can never overwrite a good stored value with junk.
  // An explicit null is kept — that's the operator clearing a field.
  mapAppToDb(settings) {
    const row = this.buildDbRow(settings);
    return Object.fromEntries(
      Object.entries(row).filter(
        ([, v]) => v !== undefined && !(typeof v === 'number' && Number.isNaN(v))
      )
    );
  },

  buildDbRow(settings) {
    return {
      business_name: settings.businessName,
      email: settings.email,
      phone: settings.phone,
      address: settings.address,
      business_hours: settings.businessHours,
      sales_tax_rate: settings.salesTaxRate,
      doc_fee: settings.docFee,
      default_finance_rate: settings.defaultFinanceRate,
      email_notifications: settings.emailNotifications,
      sms_notifications: settings.smsNotifications,
      appointment_reminders: settings.appointmentReminders,
      facebook: settings.facebook,
      instagram: settings.instagram,
      twitter: settings.twitter,
      youtube: settings.youtube,
      meta_title: settings.metaTitle,
      meta_description: settings.metaDescription,
      keywords: settings.keywords,
    };
  },
  // Get all settings
  async getSettings() {
    try {
      const { data, error } = await supabase
        .from(SETTINGS_TABLE)
        .select('*')
        .single();
      
      // If table missing (42P01) or no rows (PGRST116), return defaults
      if (error && (error.code === 'PGRST116' || error.code === '42P01')) {
        return { data: this.getDefaultSettings(), error: null };
      }

      // Merge with defaults and map to camelCase to ensure required fields exist
      const defaults = this.getDefaultSettings();
      const mapped = data ? this.mapDbToApp(data) : null;
      const merged = mapped
        ? { ...defaults, ...mapped, businessHours: { ...defaults.businessHours, ...(mapped.businessHours || {}) } }
        : defaults;

      return { data: merged, error: null };
    } catch (err) {
      console.error('Error fetching settings:', err);
      return { data: null, error: err };
    }
  },

  // Save settings
  async saveSettings(settings) {
    try {
      // First check if settings exist
      const { data: existing } = await supabase
        .from(SETTINGS_TABLE)
        .select('id')
        .single();

      let result;
      if (existing) {
        // Update existing settings
        result = await supabase
          .from(SETTINGS_TABLE)
          .update({
            ...this.mapAppToDb(settings),
            updated_at: new Date().toISOString()
          })
          .eq('id', existing.id)
          .select()
          .single();
      } else {
        // Insert new settings
        result = await supabase
          .from(SETTINGS_TABLE)
          .insert({
            ...this.mapAppToDb(settings),
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          })
          .select()
          .single();
      }

      return result;
    } catch (err) {
      console.error('Error saving settings:', err);
      return { data: null, error: err };
    }
  },

  // Get default settings
  getDefaultSettings() {
    return {
      // Business Information
      businessName: 'Paradise-Performance',
      email: 'sales@paradise-performance.com',
      phone: '(817) 382-2316',
      address: '305 Co Rd 3470, Paradise, TX 76073',
      
      // Business Hours
      businessHours: {
        monday: { open: '8:00 AM', close: '5:00 PM' },
        tuesday: { open: '8:00 AM', close: '5:00 PM' },
        wednesday: { open: '8:00 AM', close: '5:00 PM' },
        thursday: { open: '8:00 AM', close: '5:00 PM' },
        friday: { open: '8:00 AM', close: '5:00 PM' },
        saturday: { open: '9:00 AM', close: '4:00 PM' },
        sunday: { open: 'Closed', close: 'Closed' },
      },
      
      // Pricing Settings
      // TX motor vehicle sales tax. NOT the 8.25% general retail rate — the
      // seed in database/site-settings-setup.sql still has 8.25 and is wrong
      // for vehicle sales; every other pricing path in the app uses 6.25.
      salesTaxRate: 6.25,
      docFee: 225,
      defaultFinanceRate: 4.9,
      
      // Notification Settings
      emailNotifications: true,
      smsNotifications: false,
      appointmentReminders: true,
      
      // Social Media
      facebook: 'https://facebook.com/paradiseperformance',
      instagram: 'https://instagram.com/paradiseperformance',
      twitter: '',
      youtube: '',
      
      // SEO Settings
      metaTitle: 'Paradise-Performance - Golf Carts, Vehicles & Trailers',
      metaDescription: 'Your trusted source for golf carts, vehicles, and trailers in Paradise, TX. Quality inventory, expert service, and honest deals.',
      keywords: 'golf carts, vehicles, trailers, Paradise TX, automotive, dealership'
    };
  }
};

export default settingsService;
